import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Users, CreditCard, Calendar, Activity } from 'lucide-react';

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('7d');

  // Mock data for charts
  const revenueData = [
    { name: 'Mon', revenue: 2400, bookings: 24 },
    { name: 'Tue', revenue: 1398, bookings: 13 },
    { name: 'Wed', revenue: 9800, bookings: 98 },
    { name: 'Thu', revenue: 3908, bookings: 39 },
    { name: 'Fri', revenue: 4800, bookings: 48 },
    { name: 'Sat', revenue: 3800, bookings: 38 },
    { name: 'Sun', revenue: 4300, bookings: 43 }
  ];

  const userGrowthData = [
    { name: 'Jan', customers: 65, providers: 12 },
    { name: 'Feb', customers: 78, providers: 18 },
    { name: 'Mar', customers: 92, providers: 24 },
    { name: 'Apr', revenue: 3908, bookings: 39 },
    { name: 'May', customers: 145, providers: 38 },
    { name: 'Jun', customers: 167, providers: 45 },
    { name: 'Jul', customers: 189, providers: 52 }
  ];

  const serviceCategories = [
    { name: 'Cleaning', value: 35, color: '#8884d8' },
    { name: 'Plumbing', value: 25, color: '#82ca9d' },
    { name: 'Electrical', value: 20, color: '#ffc658' },
    { name: 'Gardening', value: 15, color: '#ff7c7c' },
    { name: 'Other', value: 5, color: '#8dd1e1' }
  ];

  const MetricCard = ({ title, value, change, icon: Icon, color = "blue" }) => (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          <div className="flex items-center mt-2">
            {change > 0 ? (
              <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
            )}
            <span className={`text-sm font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? '+' : ''}{change}%
            </span>
            <span className="text-sm text-gray-500 ml-1">vs last period</span>
          </div>
        </div>
        <div className={`p-3 rounded-full ${color === 'green' ? 'bg-green-100' : color === 'orange' ? 'bg-orange-100' : color === 'purple' ? 'bg-purple-100' : 'bg-blue-100'}`}>
          <Icon className={`h-6 w-6 ${color === 'green' ? 'text-green-600' : color === 'orange' ? 'text-orange-600' : color === 'purple' ? 'text-purple-600' : 'text-blue-600'}`} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Track platform performance and user engagement</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Revenue"
          value="€45,231"
          change={12.5}
          icon={CreditCard}
          color="green"
        />
        <MetricCard
          title="New Users"
          value="1,234"
          change={8.2}
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Active Bookings"
          value="89"
          change={-3.1}
          icon={Calendar}
          color="orange"
        />
        <MetricCard
          title="Platform Activity"
          value="94.2%"
          change={5.7}
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Revenue & Bookings Chart */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Revenue & Bookings Trend</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Bar yAxisId="left" dataKey="revenue" fill="#3b82f6" name="Revenue (€)" />
              <Bar yAxisId="right" dataKey="bookings" fill="#10b981" name="Bookings" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">User Growth</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="customers" stroke="#3b82f6" strokeWidth={2} name="Customers" />
                <Line type="monotone" dataKey="providers" stroke="#10b981" strokeWidth={2} name="Providers" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Categories */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Service Categories</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={serviceCategories}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {serviceCategories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Top Performing Providers</h3>
          <div className="space-y-3">
            {[
              { name: 'Praha Cleaning Service', bookings: 45, rating: 4.9 },
              { name: 'Brno Plumbing Masters', bookings: 38, rating: 4.8 },
              { name: 'Ostrava Electric Solutions', bookings: 32, rating: 4.7 }
            ].map((provider, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{provider.name}</p>
                  <p className="text-sm text-gray-600">{provider.bookings} bookings</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">★ {provider.rating}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {[
              { action: 'New booking', time: '2 min ago', type: 'booking' },
              { action: 'Provider approved', time: '15 min ago', type: 'provider' },
              { action: 'Payment received', time: '32 min ago', type: 'payment' },
              { action: 'Review submitted', time: '1 hour ago', type: 'review' }
            ].map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`h-2 w-2 rounded-full ${
                  activity.type === 'booking' ? 'bg-blue-500' :
                  activity.type === 'provider' ? 'bg-green-500' :
                  activity.type === 'payment' ? 'bg-purple-500' : 'bg-yellow-500'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-xs text-gray-500">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4">System Performance</h3>
          <div className="space-y-4">
            {[
              { metric: 'API Response Time', value: '120ms', status: 'good' },
              { metric: 'Database Queries', value: '1.2K/min', status: 'good' },
              { metric: 'Error Rate', value: '0.02%', status: 'excellent' },
              { metric: 'Uptime', value: '99.9%', status: 'excellent' }
            ].map((metric, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{metric.metric}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">{metric.value}</span>
                  <div className={`h-2 w-2 rounded-full ${
                    metric.status === 'excellent' ? 'bg-green-500' :
                    metric.status === 'good' ? 'bg-blue-500' : 'bg-yellow-500'
                  }`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;